import type _jquery from "jquery";
import { provide, type MaybeRefOrGetter } from "vue";
import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord,
} from "@mcwd/util-vue";
import type { FormWidgetData } from "../../widgetDataTs.js";
import { useGatingLevel } from "./use-gating-level.js";
import { useFormEvents } from "./use-form-events.js";
import { useHiddenFieldInitializer } from "./use-hidden-field-initializer.js";
import { FormTrackingManager } from "../form-logic/FormTrackingManager.js";
import type { FormTrackingName } from "../form-settings/form-experience/form-experience-settings.js";
import { exhaustiveGuard, type Nil } from "@mcwd/typescript-type-guards";
import { toValue } from "vue";
import { useFormLogger } from "./use-form-logger.js";
import { useFormUserInputValues } from "./use-form-user-input-values.js";
import type { MarketoVals, UserMarketoInputName } from "../types/definitions/inputs/marketo-input-names-and-values.js";


type UseFormTrackingInjectionValues = InjectionValuesRecord<{
  trackingManager: FormTrackingManager
}>;

type UseFormTrackingInjectionKeys = InjectionKeysRecord<UseFormTrackingInjectionValues>;

const UseFormTrackingInjectionKeys = CreateInjectionKeysRecord<UseFormTrackingInjectionKeys>({
  trackingManager: "trackingManager"
});


export function initFormTracking({ formTrackingName, isInModal, widgetData }: { 
  formTrackingName: FormTrackingName;
  widgetData: MaybeRefOrGetter<FormWidgetData>;
  isInModal: MaybeRefOrGetter<boolean> | Nil;
}) {
  if (checkValuesAlreadyProvided(UseFormTrackingInjectionKeys)) {
    return;
  }

  const { hiddenFieldValueBuilder } = useHiddenFieldInitializer({ callerLabel: "initFormTracking" });
  const { CurrentMarketoFormGatingState } = useGatingLevel({
    callerLabel: "initFormTracking"
  });

  const { userInputValues } = useFormUserInputValues({ callerLabel: "initFormTracking" }) as { userInputValues: Pick<MarketoVals, UserMarketoInputName> };

  const trackingManager = new FormTrackingManager({
    formTrackingName,
    widgetData,
    gatingState: CurrentMarketoFormGatingState,
    hiddenFieldValuesObject: hiddenFieldValueBuilder.hiddenFieldValuesObject,
    isInModal,
    userInputValues
  });

  const events = useFormEvents({ callerLabel: "initFormTracking" });

  if (toValue(trackingManager.isInModal)) {
    trackingManager.FormOpen();
  }

  events.completed.on(({ gatingState }) => {
    if (gatingState === "BlindSubmit") {
      trackingManager.UngatedFormComplete();
    }
    else if (gatingState === "FullGate" || gatingState === "PartialGate") {
      trackingManager.FormComplete();
    }
    else {
      exhaustiveGuard(gatingState);
    }
  });

  const userFirstInteractionHandler = () => {
    events.userFirstInteraction.off(userFirstInteractionHandler);
    trackingManager.FormUserStart();
  };
  events.userFirstInteraction.on(userFirstInteractionHandler);

  events.validationErrorOnSubmit.on(() => {
    trackingManager.FormValidationError();
  });

  provide(UseFormTrackingInjectionKeys.trackingManager, trackingManager);
}

export function useFormTracking({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger();
  try {
    callerLabel ??= "useFormTracking";

    const trackingManager = injectRequiredWithSelf(UseFormTrackingInjectionKeys.trackingManager);
    return { trackingManager };
  } catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? "unknown"}`);
    throw err;
  }
}
